// (e.g., in src/redux/reducers/liveMapReducer.js)
import {
    FETCH_AGENCIES_REQUEST, FETCH_AGENCIES_SUCCESS, FETCH_AGENCIES_FAILURE,
    SELECT_AGENCY,
    CONNECT_VEHICLE_SOCKET_SUCCESS,
    UPDATE_VEHICLES,
    UPDATE_EVP_VEHICLES,
    UPDATE_EVP_INCIDENTS,
    UPDATE_EVP_PRIORITY,
    UPDATE_EVP_ROUTE_PREDICTION,
    CLEAR_EVP_ROUTE_PREDICTION,
    CONNECT_SIGNAL_SOCKET_SUCCESS,
    UPDATE_SIGNALS,
    CONNECT_PRIORITY_SOCKET_SUCCESS,
    UPDATE_PRIORITY,
    FETCH_ROUTES_REQUEST, FETCH_ROUTES_SUCCESS, FETCH_ROUTES_FAILURE,
    FETCH_ROUTE_SIGNALS_REQUEST, FETCH_ROUTE_SIGNALS_SUCCESS, FETCH_ROUTE_SIGNALS_FAILURE,
    FETCH_STOPS_REQUEST, FETCH_STOPS_SUCCESS, FETCH_STOPS_FAILURE,
    SELECT_ROUTES,
    FETCH_LATENCY_REQUEST, FETCH_LATENCY_SUCCESS, FETCH_LATENCY_FAILURE,
    UPDATE_LATENCY,
    SET_LATENCY_ENABLED,
    BATCH_UPDATE_ALL_ENTITIES,
} from './map-types';


/**
 * Helper function to compute allowed signal IDs based on route selection
 * @param {Object} selectedRoutes - Map of route IDs to boolean (selected = true)
 * @param {Object} routeSignalsByAgency - Route signals data by agency
 * @param {Array} selectedAgencyAbbreviations - List of selected agency abbreviations
 * @returns {Set|null} - Set of allowed signal IDs or null (show all)
 */
const computeAllowedSignalIdsFromState = (selectedRoutes, routeSignalsByAgency, selectedAgencyAbbreviations) => {
    const selectedRouteIds = Object.keys(selectedRoutes).filter(routeId => selectedRoutes[routeId]);
    
    if (selectedRouteIds.length === 0) {
        // No routes selected, show all signals
        return null;
    }
    
    // Collect all signal IDs from selected routes across all agencies
    const signalIds = new Set();
    selectedRouteIds.forEach(routeId => {
        // Check all selected agencies for this route
        selectedAgencyAbbreviations.forEach(agencyAbbr => {
            const agencyLower = agencyAbbr.toLowerCase();
            const agencyRoutes = routeSignalsByAgency[agencyLower];
            const routeSignalData = agencyRoutes && agencyRoutes[routeId];
            
            if (routeSignalData && routeSignalData.signals) {
                routeSignalData.signals.forEach(routeSignal => {
                    // Ensure it's stored as a string to match signal.id format
                    signalIds.add(String(routeSignal.signal_id));
                });
            }
        });
    });
    
    return signalIds;
};

const initialState = {
    agencies: {
        data: [], // List of agencies
        isLoading: true,
        error: null,
        selectedAgency: null, // Simple string holding the selected agency name
        selectedAgencyAbbreviations: [], // List of the agency abbreviations associated with selected agency name
        selectedPartnerAbbreviations: [], // List of partner abbreviations for signals filtering
        hasEvpAgencies: false, // Whether the selected agency has emergency (EVP) child agencies
    },
    vehicles: {
        byAgency: {}, // Nested structure: { agencyId: { vehicleId: vehicleData } }
    },
    evpVehicles: {
        byAgency: {}, // Nested structure: { agencyId: { vehicleId: evpVehicleData } }
    },
    evpIncidents: {
        byAgency: {}, // Nested structure: { agencyId: { incidentId: evpIncidentData } }
    },
    evpPriority: {
        bySignal: {},      // { signal_id: { priorityId: priorityData } }
        byVehicle: {},     // { vehicle_id: { priorityId: priorityData } } — reverse index from vehicle_ids
        allRecords: {},    // { priorityId: priorityData }
        sortedBySignal: {}, // { signal_id: { reqs: [...], time: timestamp } }
        sortedByVehicle: {}, // { vehicle_id: { reqs: [...], time: timestamp } }
        lastUpdated: null,
        activeCount: 0,
    },
    evpRoutePredictions: {
        // Keyed by composite "${agency}_${vehicleId}" for O(1) lookup
        // Each entry: { route_id, coordinates: [[lng,lat],...], created_at, agency, vehicleId }
        byVehicle: {},
    },
    signals: {
        byAgency: {}, // Nested structure: { agencyId: { signalId: signalData } }
        
        /* SIGNAL DATA STRUCTURE (after normalization - based on actual API/websocket samples):
         * {
         *   // CORE FIELDS (always present)
         *   id: "287",
         *   signalId: "287", 
         *   entity_type: "signal",
         *   agency: "PSV",
         *   dateLoad: 1755154711843,
         *   
         *   // API FIELDS (from actual API response sample)
         *   lat: 37.356976,                   // from latitude
         *   lng: -121.846839,                 // from longitude  
         *   status: "operational",            // from Operational boolean
         *   intersection: "MAXTIME: Alum Rock Ave & Jose Figueres Ave", // from intersection_name
         *   city_assigned_id: "10044",
         *   evp_channel_list: [],
         *   geometry: [{bearing: 238.1, lanes: 1, lat: 37.357042, lng: -121.846745, phase: 1}],
         *   maestro_id: "NUC7i3BNK966B-0",
         *   partner_abbreviation: "psv",
         *   phase_list: [1,2,3,4,5,6,7,8],
         *   phase_overlap_list: [],
         *   private_ip: "192.168.1.90:10044",
         *   signal_type_id: "MaxTime",
         *   
         *   // WEBSOCKET FIELDS (from actual websocket sample)
         *   dataPed: [],
         *   dataTraffic: [{phase: 1, lat: 37.341117, lng: -121.841384, color: "#FF0000", active: true, bearing: 225.6}]
         * }
         */
    },
    priority: {
        // OPTIMIZED STRUCTURE FOR O(1) RENDERING LOOKUPS
        byVehicle: {}, // Direct lookup by bus_id: { bus_id: { priorityId: priorityData, ... } }
        bySignal: {}, // Direct lookup by signal_id: { signal_id: { priorityId: priorityData, ... } }
        
        // METADATA for cleanup and management
        allRecords: {}, // All priority records by ID: { priorityId: priorityData }
        lastUpdated: null, // Timestamp of last update
        activeCount: 0, // Count of active priority requests
        sortedByVehicle: {}, // Sorted priority requests by vehicle
        sortedBySignal: {}, // Sorted priority requests by signal
        
        /* PRIORITY DATA STRUCTURE (after normalization):
         * {
         *   id: "agency_2600_0164",           // Generated: ${agencyId}_${signal_id}_${bus_id}
         *   entity_type: "priority",          // Entity identifier
         *   signal_id: 2600,                  // Raw signal ID (number)
         *   bus_id: "0164",                   // Raw vehicle/bus ID (string)
         *   eta: 26,                          // Estimated time of arrival (seconds)
         *   time: 1755154711.843,             // Unix timestamp
         *   channel: 1,                       // TSP channel number
         *   category: "ACTIVE",               // Request category
         *   status: "Active",                 // Current status
         *   state: "Early",                   // TSP state (Early/On-time/Late)
         *   future: "F",                      // Future flag
         *   agency: "psv",                    // Agency identifier (lowercase)
         *   intersection: "Main St & 1st Ave" // Intersection name (from signals store)
         * }
         */
    },
    latency: {
        enabled: false, // Whether latency layer is visible on map (controlled by map toggle)
        bySignal: {}, // Latency data indexed by signal ID: { signalId: latencyData }
        geojson: null, // GeoJSON for rendering latency heat map
        isLoading: false,
        error: null,
        lastUpdated: null,
    },
    connectionStatus: 'disconnected', // disconnected, connecting, connected, error
    signalConnectionStatus: 'disconnected', // disconnected, connecting, connected, error
    tspConnectionStatus: 'disconnected',
    routes: {
        geojson: null, // GeoJSON for routes
        routeColors: {}, // Map of route IDs to colors
        allRoutes: {}, // Metadata about routes
        isLoading: false,
        error: null,
        selectedIds: {}, // Map of selected route IDs for O(1) lookup - true means selected
    },
    routeSignals: {
        byAgency: {}, // Map of agencies to route signals: { agencyId: { routeId: routeSignalsResponse } }
        allowedSignalIds: null, // Set of signal IDs that should be visible based on route selection (null = show all)
        isLoading: false,
        error: null,
        lastFetched: null, // Track which agency:route was last fetched (e.g., "vta:route_1")
    },
    stops: {
        geojson: null, // GeoJSON for stops
        stopsById: {}, // Map of stop IDs to complete stop data (not just names)
        stopsByRouteId: {}, // Map of route IDs to arrays of stop IDs
        routesByStopId: {}, // Map of stop IDs to arrays of route IDs
        isLoading: false,
        error: null,
    },
    error: null, // Global error state
};

export default function liveMapReducer(state = initialState, action) {
    switch (action.type) {
        case FETCH_AGENCIES_REQUEST:
            return {
                ...state,
                agencies: {
                    ...state.agencies,
                    isLoading: true,
                    error: null,
                },
                error: null,
            };
        case FETCH_AGENCIES_SUCCESS:
            return {
                ...state,
                agencies: {
                    data: action.payload, // Assuming payload is the array of agencies
                    isLoading: false,
                    error: null,
                    selectedAgency: state.agencies.selectedAgency, // Preserve selection
                    selectedAgencyAbbreviations: state.agencies.selectedAgencyAbbreviations, // Preserve selections
                    selectedPartnerAbbreviations: state.agencies.selectedPartnerAbbreviations || [], // Preserve partner abbreviations
                },
            };
        case FETCH_AGENCIES_FAILURE:
            return {
                ...state,
                agencies: {
                    ...state.agencies,
                    isLoading: false,
                    error: action.payload, // Assuming payload is the error message/object
                },
                error: action.payload, // Also update global error
            };
        case SELECT_AGENCY: {
            // Handle agency selection - payload is now directly the agency name string
            const newSelectedAgency = action.payload; // Now directly a string or null
            

            // Find the selected agency data
            const selectedAgencyData = newSelectedAgency ? 
                state.agencies.data.find(agency => agency.name === newSelectedAgency) : null;
            
            // Get agency abbreviations for vehicles
            const newSelectedAgencyAbbreviations = selectedAgencyData ? 
                selectedAgencyData.agencies.map(agency => agency.agency_abbreviation?.toLowerCase()) || [] 
                : [];
            
            // Get partner abbreviations for signals
            const newPartnerAbbreviations = selectedAgencyData && selectedAgencyData.partner_abbreviations ? 
                selectedAgencyData.partner_abbreviations
                    .filter(partnerAbbr => partnerAbbr) // Filter out null/empty
                    .map(partnerAbbr => partnerAbbr.toLowerCase()) // Convert to uppercase to match incoming data
                : [];

            // Determine if this agency has emergency (EVP) child agencies
            const hasEvpAgencies = selectedAgencyData?.agencies?.some(
                a => a.agency_type === 'emergency'
            ) || false;
            

            let vehiclesSlice = state.vehicles;
            let evpVehiclesSlice = state.evpVehicles;
            let evpIncidentsSlice = state.evpIncidents;
            let evpPrioritySlice = state.evpPriority;
            let evpRoutePredictionsSlice = state.evpRoutePredictions;
            let signalsSlice = state.signals;
            let prioritySlice = state.priority;
            const agencyChanged = state.agencies.selectedAgency !== newSelectedAgency;
            
            if (agencyChanged) {
                // Clear all agency-specific data when switching agencies
                vehiclesSlice = {
                    ...initialState.vehicles,
                    byAgency: {},
                };
                evpVehiclesSlice = {
                    ...initialState.evpVehicles,
                    byAgency: {},
                };
                evpIncidentsSlice = {
                    ...initialState.evpIncidents,
                    byAgency: {},
                };
                evpPrioritySlice = {
                    ...initialState.evpPriority,
                    bySignal: {},
                    byVehicle: {},
                    allRecords: {},
                    sortedBySignal: {},
                    sortedByVehicle: {},
                    lastUpdated: null,
                    activeCount: 0,
                };
                evpRoutePredictionsSlice = {
                    ...initialState.evpRoutePredictions,
                    byVehicle: {},
                };
                signalsSlice = {
                    ...initialState.signals,
                    byAgency: {},
                };
                prioritySlice = {
                    ...initialState.priority,
                    byVehicle: {},
                    bySignal: {},
                    allRecords: {},
                    sortedByVehicle: {},
                    sortedBySignal: {},
                };
            }
            

            return {
                ...state,
                vehicles: {
                    ...vehiclesSlice
                },
                evpVehicles: {
                    ...evpVehiclesSlice
                },
                evpIncidents: {
                    ...evpIncidentsSlice
                },
                evpPriority: {
                    ...evpPrioritySlice
                },
                evpRoutePredictions: {
                    ...evpRoutePredictionsSlice
                },
                signals: {
                    ...signalsSlice
                },
                priority: {
                    ...prioritySlice
                },
                agencies: {
                    ...state.agencies,
                    selectedAgency: newSelectedAgency,
                    selectedAgencyAbbreviations: newSelectedAgencyAbbreviations,
                    selectedPartnerAbbreviations: newPartnerAbbreviations,
                    hasEvpAgencies,
                },
                // Reset routes and stops data when changing agency
                routes: {
                    ...initialState.routes,
                },
                stops: {
                    ...initialState.stops,
                },
                // Reset latency data when agency changes
                latency: agencyChanged ? {
                    ...initialState.latency,
                } : state.latency,
            };
        }
        case CONNECT_VEHICLE_SOCKET_SUCCESS:
            return {
                ...state,
                connectionStatus: 'connected',
            };
        case UPDATE_VEHICLES: {
            // Payload should be an object { vehicleId: vehicleData } from the throttle buffer
            const newByAgency = { ...state.vehicles.byAgency };
            const updatedIds = Object.keys(action.payload);
            const selectedAgencies = state.agencies.selectedAgencyAbbreviations || [];

            updatedIds.forEach(id => {
                const vehicleData = action.payload[id];
                const agencyId = (vehicleData.agency || 'unknown').toLowerCase(); // Store agency in lowercase
                const vehicleId = vehicleData.id; // Use the raw vehicle ID (4319)
                
                // Only process vehicles from currently selected agencies
                if (!selectedAgencies.includes(vehicleData.agency?.toLowerCase())) {
                    return; // Skip this vehicle
                }
                
                // Initialize agency object if it doesn't exist
                if (!newByAgency[agencyId]) {
                    newByAgency[agencyId] = {};
                }
                
                // Store vehicle by raw vehicle ID under agency: { "psvvta": { "4319": vehicleData } }
                newByAgency[agencyId][vehicleId] = {
                    ...newByAgency[agencyId]?.[vehicleId], // Keep potential old data not in the update
                    ...vehicleData, // Apply new data
                };
            });            

            return {
                ...state,
                vehicles: {
                    byAgency: newByAgency,
                },
            };
        }

        case UPDATE_EVP_VEHICLES: {
            // Payload: { agencyId: { vehicleId: evpVehicleData } }
            const newEvpByAgency = { ...state.evpVehicles.byAgency };

            Object.entries(action.payload).forEach(([agencyId, vehicles]) => {
                if (!newEvpByAgency[agencyId]) {
                    newEvpByAgency[agencyId] = {};
                }
                Object.entries(vehicles).forEach(([vehicleId, vehicleData]) => {
                    newEvpByAgency[agencyId][vehicleId] = {
                        ...newEvpByAgency[agencyId]?.[vehicleId],
                        ...vehicleData,
                    };
                });
            });

            return {
                ...state,
                evpVehicles: {
                    byAgency: newEvpByAgency,
                },
            };
        }

        case UPDATE_EVP_INCIDENTS: {
            // Payload: { agencyId: { incidentId: evpIncidentData } }
            const newIncidentsByAgency = { ...state.evpIncidents.byAgency };

            Object.entries(action.payload).forEach(([agencyId, incidents]) => {
                if (!newIncidentsByAgency[agencyId]) {
                    newIncidentsByAgency[agencyId] = {};
                }
                Object.entries(incidents).forEach(([incidentId, incidentData]) => {
                    newIncidentsByAgency[agencyId][incidentId] = {
                        ...newIncidentsByAgency[agencyId]?.[incidentId],
                        ...incidentData,
                    };
                });
            });

            return {
                ...state,
                evpIncidents: {
                    byAgency: newIncidentsByAgency,
                },
            };
        }

        case UPDATE_EVP_PRIORITY: {
            // Payload: { agencyId: { recordId: evpPriorityData } }
            // Structure mirrors UPDATE_PRIORITY but stored in evpPriority slice
            const evpPriorityUpdates = action.payload;

            const newBySignal = { ...state.evpPriority.bySignal };
            const newAllRecords = { ...state.evpPriority.allRecords };

            // Clear existing records for updating agencies, then add new data
            Object.keys(evpPriorityUpdates).forEach(agencyId => {
                const agencyData = evpPriorityUpdates[agencyId];

                // Remove old data for this agency
                Object.keys(newAllRecords).forEach(recordId => {
                    const record = newAllRecords[recordId];
                    if (record.agency && record.agency.toLowerCase() === agencyId.toLowerCase()) {
                        delete newAllRecords[recordId];
                        if (record.signal_id && newBySignal[record.signal_id]) {
                            delete newBySignal[record.signal_id][recordId];
                            if (Object.keys(newBySignal[record.signal_id]).length === 0) {
                                delete newBySignal[record.signal_id];
                            }
                        }
                    }
                });

                // Add new data
                Object.entries(agencyData).forEach(([recordId, priorityRecord]) => {
                    const priorityId = priorityRecord.id || recordId;
                    newAllRecords[priorityId] = priorityRecord;

                    if (priorityRecord.signal_id) {
                        if (!newBySignal[priorityRecord.signal_id]) {
                            newBySignal[priorityRecord.signal_id] = {};
                        }
                        newBySignal[priorityRecord.signal_id][priorityId] = priorityRecord;
                    }
                });
            });

            // Build sorted structure by signal
            const sortedBySignal = {};
            const signalGroups = new Map();

            Object.values(newAllRecords).forEach(record => {
                if (record.signal_id) {
                    if (!signalGroups.has(record.signal_id)) {
                        signalGroups.set(record.signal_id, []);
                    }
                    signalGroups.get(record.signal_id).push(record);
                }
            });

            signalGroups.forEach((reqs, signalId) => {
                // Sort by channel number ascending
                const sorted = [...reqs].sort((a, b) => (a.channel || 0) - (b.channel || 0));
                if (sorted.length > 0) {
                    sortedBySignal[signalId] = { reqs: sorted, time: Date.now() };
                }
            });

            // Build byVehicle reverse index
            const newByVehicle = {};
            Object.values(newAllRecords).forEach(record => {
                if (Array.isArray(record.vehicle_ids)) {
                    record.vehicle_ids.forEach(vid => {
                        if (!newByVehicle[vid]) {
                            newByVehicle[vid] = {};
                        }
                        newByVehicle[vid][record.id] = record;
                    });
                }
            });

            // Build sortedByVehicle
            const sortedByVehicle = {};
            Object.entries(newByVehicle).forEach(([vid, records]) => {
                const sorted = Object.values(records).sort((a, b) => (a.channel || 0) - (b.channel || 0));
                if (sorted.length > 0) {
                    sortedByVehicle[vid] = { reqs: sorted, time: Date.now() };
                }
            });

            return {
                ...state,
                evpPriority: {
                    bySignal: newBySignal,
                    byVehicle: newByVehicle,
                    allRecords: newAllRecords,
                    sortedBySignal,
                    sortedByVehicle,
                    lastUpdated: new Date().toISOString(),
                    activeCount: Object.keys(newAllRecords).length,
                },
            };
        }

        case UPDATE_EVP_ROUTE_PREDICTION: {
            // Payload: { key: "${agency}_${vehicleId}", route_id, geojson: GeoJSON Feature, created_at, agency, vehicleId }
            const prediction = action.payload;
            if (!prediction || !prediction.key) return state;

            return {
                ...state,
                evpRoutePredictions: {
                    ...state.evpRoutePredictions,
                    byVehicle: {
                        ...state.evpRoutePredictions.byVehicle,
                        [prediction.key]: {
                            route_id: prediction.route_id,
                            geojson: prediction.geojson,
                            created_at: prediction.created_at,
                            agency: prediction.agency,
                            vehicleId: prediction.vehicleId,
                        },
                    },
                },
            };
        }

        case CLEAR_EVP_ROUTE_PREDICTION: {
            // Payload: { key: "${agency}_${vehicleId}" } or just the key string
            const clearKey = typeof action.payload === 'string'
                ? action.payload
                : action.payload?.key;

            if (!clearKey) return state;

            const updatedByVehicle = { ...state.evpRoutePredictions.byVehicle };
            delete updatedByVehicle[clearKey];

            return {
                ...state,
                evpRoutePredictions: {
                    ...state.evpRoutePredictions,
                    byVehicle: updatedByVehicle,
                },
            };
        }

        
        // Route-related cases
        case FETCH_ROUTES_REQUEST:
            return {
                ...state,
                routes: {
                    ...state.routes,
                    isLoading: true,
                    error: null,
                },
            };
        case FETCH_ROUTES_SUCCESS:
            return {
                ...state,
                routes: {
                    geojson: action.payload.geojson,
                    routeColors: action.payload.routeColors,
                    allRoutes: action.payload.allRoutes,
                    isLoading: false,
                    error: null,
                    selectedIds: state.routes.selectedIds, // Preserve selections
                },
            };
        case FETCH_ROUTES_FAILURE:
            return {
                ...state,
                routes: {
                    ...state.routes,
                    isLoading: false,
                    error: action.payload,
                },
            };
            
        // Add case for selecting routes
        case SELECT_ROUTES: { 
            const newSelectedIds = { ...action.payload };
            
            // Compute allowed signal IDs based on new route selection
            const allowedSignalIds = computeAllowedSignalIdsFromState(
                newSelectedIds,
                state.routeSignals?.byAgency || {},
                state.agencies?.selectedAgencyAbbreviations || []
            );
            
            return {
                ...state,
                routes: {
                    ...state.routes,
                    selectedIds: newSelectedIds,
                },
                routeSignals: {
                    ...state.routeSignals,
                    allowedSignalIds: allowedSignalIds,
                },
            };
        }



        // Route signals cases
        case FETCH_ROUTE_SIGNALS_REQUEST:
            return {
                ...state,
                routeSignals: {
                    ...state.routeSignals,
                    isLoading: true,
                    error: null,
                },
            };
        case FETCH_ROUTE_SIGNALS_SUCCESS: {
            const { agencyId, data } = action.payload;
            const routeId = data.route_id || data.id;
            
            // Update route signals data first
            const newRouteSignalsByAgency = {
                ...state.routeSignals.byAgency,
                [agencyId]: {
                    ...state.routeSignals.byAgency[agencyId],
                    [routeId]: data, // Store the clean route signals data
                },
            };
            
            // Recompute allowed signal IDs with updated route signals data
            const allowedSignalIds = computeAllowedSignalIdsFromState(
                state.routes?.selectedIds || {},
                newRouteSignalsByAgency,
                state.agencies?.selectedAgencyAbbreviations || []
            );
            
            return {
                ...state,
                routeSignals: {
                    ...state.routeSignals,
                    byAgency: newRouteSignalsByAgency,
                    allowedSignalIds: allowedSignalIds,
                    isLoading: false,
                    error: null,
                    lastFetched: `${agencyId}:${routeId}`,
                },
            };
        }
        case FETCH_ROUTE_SIGNALS_FAILURE:
            return {
                ...state,
                routeSignals: {
                    ...state.routeSignals,
                    isLoading: false,
                    error: action.payload,
                },
            };

        // Stop-related cases
        case FETCH_STOPS_REQUEST:
            return {
                ...state,
                stops: {
                    ...state.stops,
                    isLoading: true,
                    error: null,
                },
            };
        case FETCH_STOPS_SUCCESS:
            return {
                ...state,
                stops: {
                    geojson: action.payload.geojson,
                    stopsById: action.payload.stopsById,
                    stopsByRouteId: action.payload.stopsByRouteId,
                    routesByStopId: action.payload.routesByStopId,
                    isLoading: false,
                    error: null,
                },
            };
        case FETCH_STOPS_FAILURE:
            return {
                ...state,
                stops: {
                    ...state.stops,
                    isLoading: false,
                    error: action.payload,
                },
            };
        
        // Signal socket connection cases
        case CONNECT_SIGNAL_SOCKET_SUCCESS:
            return {
                ...state,
                signalConnectionStatus: 'connected',
            };
        case UPDATE_SIGNALS: {
            // Payload should be an object { signalId: signalData } from the throttle buffer
            const newByAgencyForSignals = { ...state.signals.byAgency };
            const updatedIdsForSignals = Object.keys(action.payload);
            const selectedPartnerAbbreviations = state.agencies.selectedPartnerAbbreviations || [];

            updatedIdsForSignals.forEach(id => {
                const signalData = action.payload[id];
                const agencyId = (signalData.agency || 'unknown').toLowerCase(); // Store agency in lowercase
                const signalId = signalData.id; // Use the raw signal ID (287)
                
                // Only process signals from currently selected partner agencies
                if (!selectedPartnerAbbreviations.includes(signalData.agency?.toLowerCase())) {
                    return; // Skip this signal
                }
                
                // Initialize agency object if it doesn't exist
                if (!newByAgencyForSignals[agencyId]) {
                    newByAgencyForSignals[agencyId] = {};
                }
                
                // Store signal by raw signal ID under agency: { "psv": { "287": signalData } }
                newByAgencyForSignals[agencyId][signalId] = {
                    ...newByAgencyForSignals[agencyId]?.[signalId], // Keep potential old data not in the update
                    ...signalData, // Apply new data
                };
            });

            return {
                ...state,
                signals: {
                    byAgency: newByAgencyForSignals,
                },
            };
        }


        // Priority socket connection cases
        case CONNECT_PRIORITY_SOCKET_SUCCESS:
            return {
                ...state,
                tspConnectionStatus: 'connected',
            };

        // Signal Status socket connection cases
        
        case UPDATE_PRIORITY: {
            // Handle the new nested structure: { agency: { recordId: recordData } }
            // ARCHITECTURE: Selective agency-based data replacement instead of complete wipe
            // When "sjc" sends updates, only clear/replace "sjc" data, keep "stc" data intact
            const priorityUpdates = action.payload;
            const selectedAgencies = state.agencies.selectedAgencyAbbreviations || [];
            const selectedPartnerAbbreviations = state.agencies.selectedPartnerAbbreviations || [];
            
            // Start with existing data (selective replacement, not complete wipe)
            const newByVehicle = { ...state.priority.byVehicle };
            const newBySignal = { ...state.priority.bySignal };
            const newAllRecords = { ...state.priority.allRecords };
            
            // Keep track of agencies being updated to remove their old data
            const updatingAgencies = new Set();
            
            // Process each agency's priority data
            Object.keys(priorityUpdates).forEach(agencyId => {
                // Only process priority data from currently selected agencies
                const agencyLower = agencyId.toLowerCase();
                if (!selectedAgencies.includes(agencyLower) && !selectedPartnerAbbreviations.includes(agencyLower)) {
                    return; // Skip this agency's priority data
                }
                
                updatingAgencies.add(agencyLower);
                const agencyPriorityData = priorityUpdates[agencyId];
                
                // Remove old data for this specific agency only
                Object.keys(newAllRecords).forEach(recordId => {
                    const record = newAllRecords[recordId];
                    if (record.agency && record.agency.toLowerCase() === agencyLower) {
                        // Remove from allRecords
                        delete newAllRecords[recordId];
                        
                        // Remove from byVehicle index
                        if (record.bus_id && newByVehicle[record.bus_id]) {
                            delete newByVehicle[record.bus_id][recordId];
                            if (Object.keys(newByVehicle[record.bus_id]).length === 0) {
                                delete newByVehicle[record.bus_id];
                            }
                        }
                        
                        // Remove from bySignal index
                        if (record.signal_id && newBySignal[record.signal_id]) {
                            delete newBySignal[record.signal_id][recordId];
                            if (Object.keys(newBySignal[record.signal_id]).length === 0) {
                                delete newBySignal[record.signal_id];
                            }
                        }
                    }
                });
                
                // Add new data for this agency
                Object.keys(agencyPriorityData).forEach(recordId => {
                    const priorityRecord = agencyPriorityData[recordId];
                    
                    // Use the provided ID or generate one
                    const priorityId = priorityRecord.id || recordId;
                    
                    // Store in allRecords
                    newAllRecords[priorityId] = priorityRecord;
                    
                    // Index by vehicle ID (bus_id) for O(1) vehicle lookups
                    if (priorityRecord.bus_id) {
                        if (!newByVehicle[priorityRecord.bus_id]) {
                            newByVehicle[priorityRecord.bus_id] = {};
                        }
                        newByVehicle[priorityRecord.bus_id][priorityId] = priorityRecord;
                    }
                    
                    // Index by signal ID for O(1) signal lookups
                    if (priorityRecord.signal_id) {
                        if (!newBySignal[priorityRecord.signal_id]) {
                            newBySignal[priorityRecord.signal_id] = {};
                        }
                        newBySignal[priorityRecord.signal_id][priorityId] = priorityRecord;
                    }
                });
            });
            
            // Apply sorting logic for priority requests by vehicle
            const sortedByVehicle = new Map();
            
            // Group all records by vehicle ID first
            Object.values(newAllRecords).forEach(record => {
                if (record.bus_id) {
                    if (!sortedByVehicle.has(record.bus_id)) {
                        sortedByVehicle.set(record.bus_id, []);
                    }
                    sortedByVehicle.get(record.bus_id).push(record);
                }
            });
            
            // Create sorted priority requests structure for vehicles
            const tsp_reqs = {};
            
            Array.from(sortedByVehicle.keys()).forEach(vid => {
                // Sort: current cycle (F) before next cycle (T), then ascending ETA.
                // Category is not used as a sort key — the tooltip / icon layers
                // inspect the full `reqs` array via shared predicates, so
                // ordering is purely about time-to-arrival.
                let reqs = [...sortedByVehicle.get(vid)].sort((a, b) => {
                    if (a.future !== b.future) {
                        return a.future === 'F' ? -1 : 1;
                    }
                    return (a.eta || 0) - (b.eta || 0);
                });

                if (reqs.length == 0) return;

                tsp_reqs[vid] = { reqs: reqs, time: (new Date()).getTime() };
            });
            
            // Apply sorting logic for priority requests by signal
            const sortedBySignal = new Map();
            
            // Group all records by signal ID first
            Object.values(newAllRecords).forEach(record => {
                if (record.signal_id) {
                    if (!sortedBySignal.has(record.signal_id)) {
                        sortedBySignal.set(record.signal_id, []);
                    }
                    sortedBySignal.get(record.signal_id).push(record);
                }
            });
            
            // Create sorted priority requests structure for signals
            const signal_priority_reqs = {};
            
            Array.from(sortedBySignal.keys()).forEach(signalId => {
                // Sort: current cycle (F) before next cycle (T), then ascending ETA.
                // Category is not used as a sort key — the tooltip / icon layers
                // inspect the full `reqs` array via shared predicates, so
                // ordering is purely about time-to-arrival.
                let reqs = [...sortedBySignal.get(signalId)].sort((a, b) => {
                    if (a.future !== b.future) {
                        return a.future === 'F' ? -1 : 1;
                    }
                    return (a.eta || 0) - (b.eta || 0);
                });

                if (reqs.length == 0) return;

                signal_priority_reqs[signalId] = { reqs: reqs, time: (new Date()).getTime() };
            });
            
            return {
                ...state,
                priority: {
                    byVehicle: newByVehicle,
                    bySignal: newBySignal,
                    allRecords: newAllRecords,
                    sortedByVehicle: tsp_reqs, // Add the sorted structure for vehicles
                    sortedBySignal: signal_priority_reqs, // Add the sorted structure for signals
                    lastUpdated: new Date().toISOString(),
                    activeCount: Object.keys(newAllRecords).length
                }
            };
        }


        
        // Latency data cases
        case FETCH_LATENCY_REQUEST:
            return {
                ...state,
                latency: {
                    ...state.latency,
                    isLoading: true,
                    error: null,
                },
            };
        
        case FETCH_LATENCY_SUCCESS:
            return {
                ...state,
                latency: {
                    ...state.latency,
                    bySignal: action.payload.bySignal,
                    geojson: action.payload.geojson,
                    isLoading: false,
                    error: null,
                    lastUpdated: new Date().toISOString(),
                },
            };
        
        case FETCH_LATENCY_FAILURE:
            return {
                ...state,
                latency: {
                    ...state.latency,
                    isLoading: false,
                    error: action.payload,
                },
            };

        case UPDATE_LATENCY: {
            // Handle real-time latency updates
            const newBySignal = { ...state.latency.bySignal };
            const features = [];
            
            Object.keys(action.payload).forEach(signalId => {
                const latencyData = action.payload[signalId];
                newBySignal[signalId] = latencyData;
                
                // Create GeoJSON feature for rendering if coordinates exist
                if (latencyData.lat && latencyData.lng) {
                    features.push({
                        type: 'Feature',
                        properties: {
                            signal_id: signalId,
                            latency_avg: latencyData.latency_avg || 0,
                            latency_max: latencyData.latency_max || 0,
                            latency_min: latencyData.latency_min || 0,
                            total_errors: latencyData.total_errors || 0,
                            success_count: latencyData.success || 0,
                            error_rate: latencyData.total_errors / (latencyData.total_errors + latencyData.success) || 0,
                            last_updated: latencyData.timestamp || Date.now(),
                        },
                        geometry: {
                            type: 'Point',
                            coordinates: [latencyData.lng, latencyData.lat]
                        }
                    });
                }
            });
            
            return {
                ...state,
                latency: {
                    ...state.latency,
                    bySignal: newBySignal,
                    geojson: {
                        type: 'FeatureCollection',
                        features
                    },
                    isLoading: false,
                    error: null,
                    lastUpdated: new Date().toISOString(),
                },
            };
        }

        case SET_LATENCY_ENABLED:
            return {
                ...state,
                latency: {
                    ...state.latency,
                    enabled: action.payload,
                },
            };

        case BATCH_UPDATE_ALL_ENTITIES: {
            // Combined batch update for all entity types
            // Payload: { vehicles: {...}, signals: {...}, signalStatus: {...}, priority: {...} }
            const { vehicles, evpVehicles, evpIncidents, signals, signalStatus, priority, evpPriority } = action.payload;
            
            let newState = { ...state };

            // Update EVP incidents if provided (live updates via vehicle socket)
            if (evpIncidents && Object.keys(evpIncidents).length > 0) {
                const newIncidentsByAgency = { ...newState.evpIncidents.byAgency };

                Object.entries(evpIncidents).forEach(([, incidentData]) => {
                    const agency = incidentData.agency?.toLowerCase();
                    const incidentId = incidentData.id;

                    if (agency && incidentId) {
                        if (!newIncidentsByAgency[agency]) {
                            newIncidentsByAgency[agency] = {};
                        }
                        newIncidentsByAgency[agency][incidentId] = {
                            ...newIncidentsByAgency[agency]?.[incidentId],
                            ...incidentData,
                        };
                    }
                });
                newState.evpIncidents = { ...newState.evpIncidents, byAgency: newIncidentsByAgency };
            }

            // Update EVP vehicles if provided
            if (evpVehicles && Object.keys(evpVehicles).length > 0) {
                const newEvpByAgency = { ...newState.evpVehicles.byAgency };

                Object.entries(evpVehicles).forEach(([, vehicleData]) => {
                    const agency = vehicleData.agency?.toLowerCase();
                    const vehicleId = vehicleData.id;

                    if (agency && vehicleId) {
                        if (!newEvpByAgency[agency]) {
                            newEvpByAgency[agency] = {};
                        }
                        newEvpByAgency[agency][vehicleId] = {
                            ...newEvpByAgency[agency]?.[vehicleId],
                            ...vehicleData,
                        };
                    }
                });
                newState.evpVehicles = { ...newState.evpVehicles, byAgency: newEvpByAgency };
            }
            
            // Update vehicles if provided
            if (vehicles && Object.keys(vehicles).length > 0) {
                const newVehiclesByAgency = { ...newState.vehicles.byAgency };
                const selectedAgencies = newState.agencies.selectedAgencyAbbreviations || [];
                
                Object.entries(vehicles).forEach(([, vehicleData]) => {
                    const agency = vehicleData.agency?.toLowerCase();
                    const vehicleId = vehicleData.id; // Use the actual vehicle ID from the data, not the composite key
                    
                    // Only process vehicles from currently selected agencies (like old system)
                    if (!selectedAgencies.includes(vehicleData.agency?.toLowerCase())) {
                        return; // Skip this vehicle
                    }
                    
                    if (agency && vehicleId) {
                        if (!newVehiclesByAgency[agency]) {
                            newVehiclesByAgency[agency] = {};
                        }
                        // Store by actual vehicle ID (e.g., "4319") not composite key (e.g., "psv_4319")
                        newVehiclesByAgency[agency][vehicleId] = {
                            ...newVehiclesByAgency[agency]?.[vehicleId], // Keep potential old data
                            ...vehicleData, // Apply new data
                        };
                    }
                });
                newState.vehicles = { ...newState.vehicles, byAgency: newVehiclesByAgency };
            }
            
            // Update signals if provided
            if (signals && Object.keys(signals).length > 0) {
                const newSignalsByAgency = { ...newState.signals.byAgency };
                Object.entries(signals).forEach(([, signalData]) => {
                    const agency = signalData.agency?.toLowerCase();
                    const signalId = signalData.id || signalData.signalId; // Use the actual signal ID from the data

                    if (agency && signalId) {
                        if (!newSignalsByAgency[agency]) {
                            newSignalsByAgency[agency] = {};
                        }

                        // Regular signal data - create or update complete record
                        newSignalsByAgency[agency][signalId] = {
                            ...newSignalsByAgency[agency]?.[signalId], // Keep potential old data
                            ...signalData, // Apply new data
                        };
                    }
                });
                newState.signals = { ...newState.signals, byAgency: newSignalsByAgency };
            }

            // Update signal status if provided (separate from regular signals)
            if (signalStatus && Object.keys(signalStatus).length > 0) {
                const newSignalsByAgency = { ...newState.signals.byAgency };
                let hasUpdates = false;

                Object.entries(signalStatus).forEach(([, signalData]) => {
                    const agency = signalData.agency?.toLowerCase();
                    const signalId = signalData.id;

                    if (agency && signalId) {
                        // Check if we have an existing signal to merge with
                        const existingSignal = newSignalsByAgency[agency]?.[signalId];

                        if (existingSignal) {
                            // Only create agency object if it doesn't exist
                            if (!newSignalsByAgency[agency]) {
                                newSignalsByAgency[agency] = {};
                            }

                            // Signal status update - merge only pedestrian bitmask fields into existing signal
                            const statusFields = {};
                            if (signalData.walks !== undefined) statusFields.walks = signalData.walks;
                            if (signalData.dont_walks !== undefined) statusFields.dont_walks = signalData.dont_walks;
                            if (signalData.ped_clears !== undefined) statusFields.ped_clears = signalData.ped_clears;
                            if (signalData.ped_calls !== undefined) statusFields.ped_calls = signalData.ped_calls;
                            if (signalData.priority !== undefined) statusFields.priority = signalData.priority;
                            // Per-channel controller preempt statuses (railroad on ch 1-2, hardware EVP on ch 3+).
                            // Indexed by channel - 1; values follow the NTCIP PREEMPT_STATUS legend.
                            if (signalData.preemption !== undefined) statusFields.preemption = signalData.preemption;

                            newSignalsByAgency[agency][signalId] = {
                                ...existingSignal,
                                ...statusFields,
                                dateLoad: Date.now()
                            };
                            hasUpdates = true;
                        }
                    }
                });

                // Only update state if we actually made changes
                if (hasUpdates) {
                    newState.signals = { ...newState.signals, byAgency: newSignalsByAgency };
                }
            }

            // Update priority if provided
            if (priority && Object.keys(priority).length > 0) {
                const selectedAgencies = newState.agencies.selectedAgencyAbbreviations || [];
                const selectedPartnerAbbreviations = newState.agencies.selectedPartnerAbbreviations || [];
                
                // Start with existing data (selective replacement, not complete wipe)
                const newAllRecords = { ...newState.priority.allRecords };
                const newByVehicle = { ...newState.priority.byVehicle };
                const newBySignal = { ...newState.priority.bySignal };
                
                // Identify which agencies are being updated in this batch
                const updatingAgencies = new Set();
                Object.values(priority).forEach(priorityData => {
                    if (priorityData && priorityData.agency) {
                        const agencyLower = priorityData.agency.toLowerCase();
                        // Only track agencies that are currently selected
                        if (selectedAgencies.includes(agencyLower) || selectedPartnerAbbreviations.includes(agencyLower)) {
                            updatingAgencies.add(agencyLower);
                        }
                    }
                });
                
                // Clear existing data for agencies being updated (TSP messages contain complete active set)
                updatingAgencies.forEach(agencyLower => {
                    Object.keys(newAllRecords).forEach(recordId => {
                        const record = newAllRecords[recordId];
                        if (record.agency && record.agency.toLowerCase() === agencyLower) {
                            // Remove from allRecords
                            delete newAllRecords[recordId];
                            
                            // Remove from byVehicle index
                            if (record.bus_id && newByVehicle[record.bus_id]) {
                                delete newByVehicle[record.bus_id][recordId];
                                if (Object.keys(newByVehicle[record.bus_id]).length === 0) {
                                    delete newByVehicle[record.bus_id];
                                }
                            }
                            
                            // Remove from bySignal index
                            if (record.signal_id && newBySignal[record.signal_id]) {
                                delete newBySignal[record.signal_id][recordId];
                                if (Object.keys(newBySignal[record.signal_id]).length === 0) {
                                    delete newBySignal[record.signal_id];
                                }
                            }
                        }
                    });
                });
                
                // Add new priority data
                Object.entries(priority).forEach(([priorityId, priorityData]) => {
                    // Add defensive check for undefined priorityData
                    if (!priorityData) {
                        return;
                    }

                    // Skip clear-agency sentinels — they already caused the per-agency
                    // wipe above (via updatingAgencies) and must not be stored as records.
                    if (priorityData.__clearAgency) {
                        return;
                    }
                    
                    // Only process priority data from currently selected agencies
                    const agencyLower = priorityData.agency?.toLowerCase();
                    if (!selectedAgencies.includes(agencyLower) && !selectedPartnerAbbreviations.includes(agencyLower)) {
                        return; // Skip this priority data
                    }
                    
                    newAllRecords[priorityId] = priorityData;
                    
                    // Update byVehicle index
                    if (priorityData.bus_id) {
                        if (!newByVehicle[priorityData.bus_id]) {
                            newByVehicle[priorityData.bus_id] = {};
                        }
                        newByVehicle[priorityData.bus_id][priorityId] = priorityData;
                    }
                    
                    // Update bySignal index
                    if (priorityData.signal_id) {
                        if (!newBySignal[priorityData.signal_id]) {
                            newBySignal[priorityData.signal_id] = {};
                        }
                        newBySignal[priorityData.signal_id][priorityId] = priorityData;
                    }
                });
                
                // Create sorted priority structures (like old system)
                const sortedByVehicle = new Map();
                const sortedBySignal = new Map();
                
                // Group priority records by vehicle and signal
                Object.values(newAllRecords).forEach(record => {
                    if (record.bus_id) {
                        if (!sortedByVehicle.has(record.bus_id)) {
                            sortedByVehicle.set(record.bus_id, []);
                        }
                        sortedByVehicle.get(record.bus_id).push(record);
                    }
                    
                    if (record.signal_id) {
                        if (!sortedBySignal.has(record.signal_id)) {
                            sortedBySignal.set(record.signal_id, []);
                        }
                        sortedBySignal.get(record.signal_id).push(record);
                    }
                });
                
                // Create sorted priority requests structure for vehicles (like old system)
                const tsp_reqs = {};
                Array.from(sortedByVehicle.keys()).forEach(vehicleId => {
                    // Sort: current cycle (F) before next cycle (T), then ascending ETA.
                    let reqs = [...sortedByVehicle.get(vehicleId)].sort((a, b) => {
                        if (a.future !== b.future) {
                            return a.future === 'F' ? -1 : 1;
                        }
                        return (a.eta || 0) - (b.eta || 0);
                    });

                    if (reqs.length == 0) return;

                    tsp_reqs[vehicleId] = { reqs: reqs, time: (new Date()).getTime() };
                });
                
                // Create sorted priority requests structure for signals (like old system)
                const signal_priority_reqs = {};
                Array.from(sortedBySignal.keys()).forEach(signalId => {
                    // Sort: current cycle (F) before next cycle (T), then ascending ETA.
                    let reqs = [...sortedBySignal.get(signalId)].sort((a, b) => {
                        if (a.future !== b.future) {
                            return a.future === 'F' ? -1 : 1;
                        }
                        return (a.eta || 0) - (b.eta || 0);
                    });

                    if (reqs.length == 0) return;

                    signal_priority_reqs[signalId] = { reqs: reqs, time: (new Date()).getTime() };
                });

                newState.priority = {
                    ...newState.priority,
                    allRecords: newAllRecords,
                    byVehicle: newByVehicle,
                    bySignal: newBySignal,
                    sortedByVehicle: tsp_reqs, // Add the sorted structure for vehicles
                    sortedBySignal: signal_priority_reqs, // Add the sorted structure for signals
                    lastUpdated: new Date().toISOString(),
                    activeCount: Object.values(newAllRecords).filter(p => p.category === 'ACTIVE').length
                };
            }

            // Update EVP preemption data if provided (from signal-status socket)
            if (evpPriority && Object.keys(evpPriority).length > 0) {
                const newEvpAllRecords = { ...newState.evpPriority.allRecords };
                const newEvpBySignal = { ...newState.evpPriority.bySignal };

                // Identify which agencies are being updated in this batch
                const evpUpdatingAgencies = new Set();
                Object.values(evpPriority).forEach(record => {
                    if (record && record.agency) {
                        evpUpdatingAgencies.add(record.agency.toLowerCase());
                    }
                });

                // Clear existing data for agencies being updated (EVP messages contain complete active set)
                evpUpdatingAgencies.forEach(agencyLower => {
                    Object.keys(newEvpAllRecords).forEach(recordId => {
                        const record = newEvpAllRecords[recordId];
                        if (record.agency && record.agency.toLowerCase() === agencyLower) {
                            delete newEvpAllRecords[recordId];
                            if (record.signal_id !== undefined && newEvpBySignal[record.signal_id]) {
                                delete newEvpBySignal[record.signal_id][recordId];
                                if (Object.keys(newEvpBySignal[record.signal_id]).length === 0) {
                                    delete newEvpBySignal[record.signal_id];
                                }
                            }
                        }
                    });
                });

                // Add new EVP preemption data
                Object.entries(evpPriority).forEach(([recordId, record]) => {
                    if (!record) return;
                    // Skip clear-agency sentinels — wipe already happened above.
                    if (record.__clearAgency) return;
                    const priorityId = record.id || recordId;
                    newEvpAllRecords[priorityId] = record;

                    if (record.signal_id !== undefined) {
                        if (!newEvpBySignal[record.signal_id]) {
                            newEvpBySignal[record.signal_id] = {};
                        }
                        newEvpBySignal[record.signal_id][priorityId] = record;
                    }
                });

                // Build sortedBySignal - group by signal, sort by channel ascending
                const evpSortedBySignal = {};
                const evpSignalGroups = new Map();

                Object.values(newEvpAllRecords).forEach(record => {
                    if (record.signal_id !== undefined) {
                        if (!evpSignalGroups.has(record.signal_id)) {
                            evpSignalGroups.set(record.signal_id, []);
                        }
                        evpSignalGroups.get(record.signal_id).push(record);
                    }
                });

                evpSignalGroups.forEach((reqs, signalId) => {
                    const sorted = [...reqs].sort((a, b) => (a.channel || 0) - (b.channel || 0));
                    if (sorted.length > 0) {
                        evpSortedBySignal[signalId] = { reqs: sorted, time: Date.now() };
                    }
                });

                // Build byVehicle reverse index: vehicle_id → { recordId: record }
                const newEvpByVehicle = {};
                Object.values(newEvpAllRecords).forEach(record => {
                    if (Array.isArray(record.vehicle_ids)) {
                        record.vehicle_ids.forEach(vid => {
                            if (!newEvpByVehicle[vid]) {
                                newEvpByVehicle[vid] = {};
                            }
                            newEvpByVehicle[vid][record.id] = record;
                        });
                    }
                });

                // Build sortedByVehicle: vehicle_id → { reqs: [...sorted by channel], time }
                const evpSortedByVehicle = {};
                Object.entries(newEvpByVehicle).forEach(([vid, records]) => {
                    const sorted = Object.values(records).sort((a, b) => (a.channel || 0) - (b.channel || 0));
                    if (sorted.length > 0) {
                        evpSortedByVehicle[vid] = { reqs: sorted, time: Date.now() };
                    }
                });

                newState.evpPriority = {
                    bySignal: newEvpBySignal,
                    byVehicle: newEvpByVehicle,
                    allRecords: newEvpAllRecords,
                    sortedBySignal: evpSortedBySignal,
                    sortedByVehicle: evpSortedByVehicle,
                    lastUpdated: new Date().toISOString(),
                    activeCount: Object.keys(newEvpAllRecords).length,
                };
            }
            
            return newState;
        }
        
        default:
            return state;
    }
}